import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  formatHumanFriendlyDate,
  Logging,
  stringToDate,
  CertificateStatus,
  UserInfo,
  UserContext,
  PermissionGroup,
  getEditPermission,
  formatDate,
  RejectionDetailModal,
  Certificate,
  CertificateToken,
  ResponseError,
  Notification,
  API,
  NotificationData,
  ConfirmationModal,
  renderCheckbox,
  ControlState,
} from '@vcd-ecm-ui/Common';
import { VtxButton, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import '../sidebyside-viewer.scss';
import SideBySideViewerHeaderProps from './SizeBySideViewerHeaderProps.interface';

const controlStateEnabled: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlStateDisabled: ControlState = {
  editable: false,
  disabled: true,
  readOnly: true,
};

const SideBySideViewerHeader = ({
  gatewayEndPoint,
  isOpen,
  editable,
  onEditCertificate,
  id,
  status,
  certificate,
  createdDateTime,
  onApprove,
  onReject,
  onClose,
  onEditCancel,
  onEditSave,
  saveEnabled,
}: SideBySideViewerHeaderProps): JSX.Element => {
  const [showRejectDetail, setShowRejectDetail] = React.useState(false);
  const [token, setToken] = React.useState(null);
  const [notification, setNotification] = React.useState(null);
  const { ccPreventEcmEdits } = useFlags();

  const [isConfirmationOpen, setIsConfirmationOpen] = React.useState(false);
  const [removeFromSync, setRemoveFromSync] = React.useState(certificate.excludeFromSync);

  const dispatch = useDispatch();

  // No valid certificate
  if (!id) {
    return (<></>);
  }

  const getStatusClass = (): string => {
    switch (status.toLocaleUpperCase()) {
      case CertificateStatus.Pending.toLocaleUpperCase():
        return 'sbsStatusPending';

      case CertificateStatus.Approved.toLocaleUpperCase():
        return 'sbsStatusApproved';

      case CertificateStatus.Rejected.toLocaleUpperCase():
        return 'sbsStatusRejected';

      case CertificateStatus.Draft.toLocaleUpperCase():
        return 'sbsStatusDraft';

      case CertificateStatus.Active.toLocaleUpperCase():
        return 'sbsStatusActive';

      case CertificateStatus.Inactive.toLocaleUpperCase():
        return 'sbsStatusInactive';

      default:
        return null;
    }
  };

  // Get the Certificate
  const getCertificateToken = () => async (): Promise<void> => {
    try {
      const apiInterface = new API(gatewayEndPoint);

      const certificateToken: CertificateToken = await apiInterface.certificate.getCertificateTokenV41(certificate.uuid);

      Logging.Info('Get Certificate Token', certificate.uuid, certificateToken);

      if (certificateToken) {
        setToken(certificateToken);
        setShowRejectDetail(true);
      }
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Certificate Token Error',
        description: `Unable to get Certificate Token '${certificate.exemptionId}'`,
        error: error as ResponseError,
      };

      Logging.Error(notificationData);

      setNotification(notificationData);
    }
  };

  const onMoreInformation = (): void => {
    // Token already Loaded
    if (token) {
      setShowRejectDetail(true);
    } else {
      // Load the Certificate Token
      dispatch(getCertificateToken());
    }
  };

  const onRejectionDetailOK = (): void => {
    setShowRejectDetail(false);
  };

  // Close the Notification
  const onNotificationClose = (): void => {
    Logging.Info('Side By Side Viewer: Notification Close');
    setNotification(null);
  };

  // Make the API Call to Update the Remove from Sync for Certificate
  const certificateRemoveFromSync = (excludeFromSync: boolean) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Update the Remove from Sync for Certificate
      await api.certificate.excludeFromSyncCertficateV41(certificate.uuid, excludeFromSync);

      setRemoveFromSync(excludeFromSync);
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Certificate Update Error',
        description: `Unable to Update Certificate '${id}'.`,
        error: error as ResponseError,
        appendMessagesForStatusCodes: [409],
        hideStatusCodes: [409],
      };

      setNotification(notificationData);
    }
  };

  // Remove from Sync Confirmation : OK
  const onOKConfirmation = (): void => {
    Logging.Debug('OK Confirmation');
    setIsConfirmationOpen(false);

    // Update the Remove from Sync for Certificate
    dispatch(certificateRemoveFromSync(!removeFromSync));
  };

  // Remove from Sync Confirmation : Cancel
  const onCancelConfirmation = (): void => {
    Logging.Debug('Cancel Confirmation');
    setIsConfirmationOpen(false);
  };

  const onRemoveFromSyncChange = (value: boolean): void => {
    Logging.Debug('Remove from Sync:', value);

    // Only show Confirmation Dialog if checkbox is checked. Not when unchecked
    if (value) {
      setIsConfirmationOpen(true);
    } else {
      setRemoveFromSync(value);

      // Update the Remove from Sync for Certificate
      dispatch(certificateRemoveFromSync(value));
    }
  };

  const confirmationMessage = 'Selecting this will remove the record from syncing to the calc engine.';

  // Get the User Context
  const userContext: UserInfo = React.useContext(UserContext);

  // Get the Certificates Edit Permission
  const permissionCertificatesEdit: boolean = getEditPermission(userContext, PermissionGroup.Certificates);

  const created: Date = stringToDate(createdDateTime);
  const showEditButton = true;
  const showMediaSize = false;
  const isPendingStatus = status ? status === CertificateStatus.Pending : true;
  const isRejectedStatus = status ? status === CertificateStatus.Rejected : true;

  return (
    <>
      <div className="sbsHeader">
        {
          editable
            ? (
              <>
                <div className="sbsHeaderLineClose">
                  {
                    showMediaSize && (
                      <>
                        <div className="sbsSize sbsXLarge">X Large</div>
                        <div className="sbsSize sbsLarge">Large</div>
                        <div className="sbsSize sbsMedium">Medium</div>
                        <div className="sbsSize sbsSmall">Small</div>
                      </>
                    )
                  }
                </div>
                <div className="sbsHeaderLinePreviousNext" />
                <div className="sbsHeaderLineInformation">
                  <div className="sbsHeaderLineInformationLeft sbsHeaderLineInformationEditLeft">
                    <div className="sbsHeaderLineInformationBase">
                      <VtxButton
                        id="editBackButton"
                        icon={<VtxIcon type="left" />}
                        type="default"
                        onClick={onEditCancel}
                      >
                        Back
                      </VtxButton>
                    </div>
                  </div>
                  <div className="sbsHeaderLineInformationEditCenter">
                    <div className="sbsHeaderLineInformationBase">
                      Edit Certificate {id}
                    </div>
                  </div>
                  <div className="sbsHeaderLineInformationRight sbsHeaderLineInformationEditRight">
                    <div className="sbsHeaderLineInformationBase">
                      <VtxButton
                        id="editSaveButton"
                        type="primary"
                        onClick={onEditSave}
                        disabled={!saveEnabled}
                      >
                        Save Edits
                      </VtxButton>
                    </div>
                  </div>
                </div>
              </>
            )
            : (
              <>
                <div className="sbsHeaderLineClose">
                  {
                    showMediaSize && (
                      <>
                        <div className="sbsSize sbsXLarge">X Large</div>
                        <div className="sbsSize sbsLarge">Large</div>
                        <div className="sbsSize sbsMedium">Medium</div>
                        <div className="sbsSize sbsSmall">Small</div>
                      </>
                    )
                  }
                  <VtxButton
                    id="sbsViewerCloseButton"
                    type="link"
                    onClick={(): void => onClose()}
                    icon={<VtxIcon type="close-circle" fontSize={22} />}
                  />
                </div>
                <div className="sbsHeaderLinePreviousNext" />
                <div className="sbsHeaderLineInformation">
                  <div className="sbsHeaderLineInformationLeft">
                    <div className="sbsHeaderLineInformationBase sbsHeaderLineInformationCertification">
                      Cert. {id}
                    </div>
                    <div className={`sbsHeaderLineInformationBase sbsHeaderLineInformationStatus ${getStatusClass()}`}>
                      {status}
                    </div>
                    <div className="sbsHeaderLineInformationBase sbsHeaderLineInformationCreated">
                      {`Created ${formatHumanFriendlyDate(created)}`}
                    </div>
                    {
                      showEditButton && permissionCertificatesEdit && (ccPreventEcmEdits !== true) && (
                        <div className="sbsHeaderLineInformationBase sbsHeaderLineInformationEdit">
                          <VtxButton
                            id="editCertificateButton"
                            icon={<VtxIcon type="edit" />}
                            type="default"
                            onClick={onEditCertificate}
                          >
                            Edit Cert.
                          </VtxButton>
                        </div>
                      )
                    }
                  </div>
                  <div className="sbsHeaderLineInformationRight">
                    {renderCheckbox('removeFromSync', 'Remove from Sync', removeFromSync, null,
                      onRemoveFromSyncChange, permissionCertificatesEdit ? controlStateEnabled : controlStateDisabled, 'certificateCheckbox')}
                    {
                      isPendingStatus && permissionCertificatesEdit && (ccPreventEcmEdits !== true) && (
                        <>
                          <div className="sbsHeaderLineInformationBase">
                            <VtxButton
                              id="approveButton"
                              icon={<VtxIcon type="check-circle" />}
                              type="primary"
                              className="greenButton"
                              onClick={onApprove}
                            >
                              Approve
                            </VtxButton>
                          </div>
                          <div className="sbsHeaderLineInformationBase">
                            <VtxButton
                              id="rejectButton"
                              icon={<VtxIcon type="stop" />}
                              type="primary"
                              className="redButton"
                              onClick={onReject}
                            >
                              Reject
                            </VtxButton>
                          </div>
                        </>
                      )
                    }
                    {
                      isRejectedStatus && (
                        <div className="rejectionDetailLink">
                          {`Rejected on ${formatDate(stringToDate(certificate.status?.statusUpdateDateTime || certificate.lastUpdatedDateTime))}`}
                          <VtxButton
                            id="rejectionDetailButton"
                            onClick={onMoreInformation}
                            type="link"
                          >
                            More Information
                          </VtxButton>
                        </div>
                      )
                    }
                  </div>
                </div>
              </>
            )
        }
      </div>
      <RejectionDetailModal
        isOpen={showRejectDetail}
        onOK={onRejectionDetailOK}
        certificate={certificate}
        token={token}
      />
      <ConfirmationModal
        isOpen={isConfirmationOpen}
        heading="Are you sure you want to continue?"
        message={confirmationMessage}
        okButtonName="Continue"
        onOK={onOKConfirmation}
        cancelButtonName="Cancel"
        onCancel={onCancelConfirmation}
        confirmationType="Warning"
      />
      <Notification
        data={notification}
        onNotificationClose={onNotificationClose}
        autoClose
      />
    </>
  );
};

export default SideBySideViewerHeader;
